import { useEffect, useRef, useState } from "react";
import { platformService } from "../../shared/services/platform.service";
import { ACTIVITY_ITEMS, LICENSE_FEATURE } from "../../shared/utils/constants";
import { getCachedFeature } from "../../shared/cache/FeatureCache";
import { arrangeActivities } from "../../shared/components/TicketConversation/TicketConversation";
import { parseIfJSON } from "../../shared/utils/helper";
import { ActivityItemProps } from "../../shared/components/TicketConversation/TicketConversation.interfaces";

export const useTicketPagePrint = (
  stateTicket: any,
) => {
  const api = new platformService();
  const [ticketActivities, setTicketActivities] = useState<ActivityItemProps[]>(
    []
  );
  const [phases, setPhases] = useState<TicketLifecyclePhase[]>([]);
  const [tasks, setTasks] = useState<TicketTask[]>([]);
  const [ticketApprovals, setTicketApprovals] = useState<TicketApproval[]>([]);
  const [isActivitiesLoading, setActivitiesLoading] = useState(false);
  const [isLifecycleLoading, setLifecycleLoading] = useState(false);
  const [isTaskApprovalLoading, setTaskApprovalLoading] = useState(false);
  const [isImagesLoading, setIsImagesLoading] = useState(false);
  const [relatedTickets, setRelatedTickets] = useState<string>("");
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const firstRender = useRef(true);
  const params: string = `?$filter=TicketId eq ${stateTicket.Id}&$expand=Status,Assignee,TicketLifecyclePhase,Team,SupportGroup,TaskCollaborators`;

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return; // skip on first render
    }

    if (!(isActivitiesLoading || isTaskApprovalLoading || isLifecycleLoading)) {
      setTimeout(() => {
        waitForImagesToLoad(() => {
          setIsImagesLoading(false);
          window.print();
        });
      }, 1000);
    }
  }, [isActivitiesLoading, isTaskApprovalLoading, isLifecycleLoading]);

  const waitForImagesToLoad = (callback) => {
    const images = Array.from(document.images);
    let loaded = 0;

    if (images.length === 0) {
      callback();
      return;
    }

    images.forEach((img) => {
      if (img.complete) {
        loaded++;
        if (loaded === images.length) callback();
      } else {
        img.addEventListener("load", () => {
          loaded++;
          if (loaded === images.length) callback();
        });
        img.addEventListener("error", () => {
          loaded++;
          if (loaded === images.length) callback();
        });
      }
    });
  };

  const loadActivities = async () => {
    try {
      setIsImagesLoading(true);
      setActivitiesLoading(true);
      let allActivities: ActivityItemProps[] = [];
      let hasMore = true;
      let page = 0;
      while (hasMore) {
        const apiResult = await api.getAllTicketActivities(stateTicket.Id, page);
        const activities = apiResult.data.value;

        allActivities = [...allActivities, ...activities];

        if (activities.length < ACTIVITY_ITEMS) {
          hasMore = false;
        }
        page++;
      }

      // Process activities with PlatformUserId in Value to replace Value with user fullname
      const userIds: number[] = [];
      allActivities.forEach(activity => {
        const parsed = parseIfJSON(activity.Value);
        if (parsed?.PlatformUserId) {
          const id = parseInt(parsed.PlatformUserId, 10);
          if (!isNaN(id) && !platformUsers.some(u => u.Id === id) && !userIds.includes(id)) {
            userIds.push(id);
          }
        }
      });
      if (userIds.length > 0) {
        try {
          const users = await api.getBulkUsersById(userIds);
          const allUsers = [...platformUsers, ...users];
          setPlatformUsers(allUsers);
          // Update activities with all users
        } catch (error) {
          console.error("Error fetching users for activities:", error);
        }
      }

      const isRelatedTicketEnabled = await getCachedFeature(
        LICENSE_FEATURE.RelatedTickets
      );
      const relatedTickets = isRelatedTicketEnabled
        ? await api.getRelatedTicketsIncludeDeleted(stateTicket.Id)
        : [];
      setTicketActivities(arrangeActivities(allActivities, relatedTickets));
      setRelatedTickets(relatedTickets.map((t) => `#${t.RelatedTicketId.toString()}`).join(", "));
      setActivitiesLoading(false);
    } catch (e) {
      setActivitiesLoading(false);
      console.error(e);
    }
  };

  const loadLifecycleData = async () => {
    try {
      setIsImagesLoading(true);
      setLifecycleLoading(true);
      const ticketLifecyclePhases = await api.getTicketLifecyclePhases(
        `?$filter=TicketLifecycleId eq ${stateTicket.TicketLifecycle?.Id}`
      );
      let promises = [
        api.getTicketApprovals(stateTicket.Id),
        api.getTicketTasks(params),
      ];
      const results = await Promise.all(promises);

      let allApprovals: TicketApprovals[] = results[0];
      allApprovals = allApprovals.map((approval) => {
        approval.Approval.TicketLifecyclePhase = ticketLifecyclePhases.find(
          (x) => x.Id == approval.Approval.TicketLifecyclePhaseId
        );
        const { TicketLifecyclePhase, ...rest } = approval.Approval;
        return { ...approval, Approval: rest };
      });

      let allTasks: TicketTask[] = results[1];
      allTasks = allTasks.map((task) => {
        task.TicketLifecyclePhase = ticketLifecyclePhases.find(
          (x) => x.Id == task.TicketLifecyclePhaseId
        );
        const { TicketLifecyclePhase, ...rest } = task;
        return rest;
      });

      if (results.length > 2) {
        setTasks(results[2]);
      }

      const arrangedPhases = ticketLifecyclePhases.sort(
        (a, b) => a.Order - b.Order
      );
      arrangedPhases.forEach((phase: TicketLifecyclePhase) => {
        phase.Tasks = allTasks.filter(
          (x) => x.TicketLifecyclePhaseId == phase.Id && !x.IsPowerAutomateTask
        );
        phase.Approvals = allApprovals.filter(
          (x) => x.Approval.TicketLifecyclePhaseId == phase.Id
        );
        phase.PowerAutomateTasks = allTasks.filter(
          (x) => x.TicketLifecyclePhaseId == phase.Id && x.IsPowerAutomateTask
        );
      });
      setPhases(arrangedPhases);
    } catch (e) {
      console.error(e);
    } finally {
      setLifecycleLoading(false);
    }
  };

  const loadTaskAndApprovals = async () => {
    try {
      setIsImagesLoading(true);
      setTaskApprovalLoading(true);
      const isTicketTaskEnabled = await getCachedFeature(
        LICENSE_FEATURE.TicketTasks
      );
      if (isTicketTaskEnabled) {
        const taskResults = await api.getTicketTasks(params);
        setTasks(taskResults);
      }
      const isTicketApprovalEnabled = await getCachedFeature(
        LICENSE_FEATURE.Approvals
      );
      if (isTicketApprovalEnabled) {
        const approvalResults = await api.getTicketApprovals(stateTicket.Id);
        setTicketApprovals(approvalResults);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setTaskApprovalLoading(false);
    }
  };
  return {
    ticketActivities,
    phases,
    tasks,
    ticketApprovals,
    loadActivities,
    loadLifecycleData,
    loadTaskAndApprovals,
    isLoading:
      isActivitiesLoading ||
      isTaskApprovalLoading ||
      isLifecycleLoading ||
      isImagesLoading,
    relatedTickets,
    platformUsers,
  };
};
